import { Flag, Pencil, Trash } from "lucide-react";
import { marked } from "marked";
import { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Spinner } from "../components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Textarea } from "../components/ui/textarea";
import { createKb } from "../services/createKb";
import { deleteKb } from "../services/deleteKb";
import { editKnowledgeBase } from "../services/editKnowledgeBase";
import { getKbs } from "../services/getKbs";

interface KnowledgeBaseForm {
  name: string;
  description: string;
  // a semi-colon delimited string of tags
  value: string;
}

const KnowledgeBase: React.FC = () => {
  const [kbs, setKbs] = useState<Array<any>>([]);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [loadingKbs, setLoadingKbs] = useState<boolean>(false);
  const [kbForm, setKbForm] = useState<KnowledgeBaseForm>({
    name: "",
    description: "",
    value: "",
  });

  const [searchQuery, setSearchQuery] = useState<string>("");

  const searchResults = () => {
    if (searchQuery === "") return kbs;
    const names = kbs.map((kb) => ({ id: kb.id, name: kb.name }));
    const descriptions = kbs.map((kb) => ({
      id: kb.id,
      description: kb.description,
    }));
    const values = kbs.map((kb) => ({ id: kb.id, value: kb.value }));

    // use regex
    const nameMatches = names.filter((it) =>
      new RegExp(searchQuery, "ig").test(it.name),
    );
    const descriptionMatches = descriptions.filter((it) =>
      new RegExp(searchQuery, "ig").test(it.description),
    );
    const valueMatches = values.filter((it) =>
      new RegExp(searchQuery, "ig").test(it.value),
    );

    const matchedIds = new Set();

    nameMatches.forEach((it) => matchedIds.add(it.id));
    descriptionMatches.forEach((it) => matchedIds.add(it.id));
    valueMatches.forEach((it) => matchedIds.add(it.id));

    return kbs.filter((it) => matchedIds.has(it.id));
  };

  const [editMode, setEditMode] = useState<boolean>(false);

  const [selectedKb, setSelectedKb] = useState<any>(null);

  async function asyncExec() {
    setLoadingKbs(true);
    const kbs = await getKbs();
    setKbs(kbs);
    setLoadingKbs(false);
  }

  useEffect(() => {
    asyncExec();
  }, []);

  return (
    <div className="px-10 w-full text-zinc-700">
      <div>
        <h1 className="mt-[50px] text-2xl">Knowledge Base</h1>
        <p className="text-zinc-500">
          Add information you want FlashDocs to be able to reference in your
          output
        </p>
      </div>

      <div className="w-full flex items-center justify-between mt-4 gap-3">
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-[600px]"
          placeholder="Search"
        />
        <Dialog
          open={openDialog}
          onOpenChange={(open) => {
            setOpenDialog(open);
          }}
        >
          <DialogTrigger>
            <Button className="shadow-md">Add Knowledge Base</Button>
          </DialogTrigger>
          <DialogContent className="min-w-[650px] text-zinc-700 overflow-x-hidden overflow-y-scroll max-h-[800px] no-scrollbar">
            <DialogHeader className="border-b py-5">
              <DialogTitle className="font-medium">
                Add to Knowledge Base
              </DialogTitle>
              <DialogDescription>
                Give FlashDocs facts to more accurately write about any topic.
              </DialogDescription>
            </DialogHeader>

            <div className="py-5">
              <div className="mb-7">
                <label htmlFor="name-template">Name</label>
                <Input
                  id="name-template"
                  placeholder="Enter the name of knowledge base"
                  value={kbForm.name}
                  onChange={(e) =>
                    setKbForm((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="mb-7">
                <label htmlFor="description-template">Description</label>
                <Input
                  id="description-template"
                  placeholder="Add a description about this knowledge base"
                  value={kbForm.description}
                  onChange={(e) =>
                    setKbForm((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="mb-7">
                <label htmlFor="value-template">Content</label>
                <Textarea
                  id="value-template"
                  placeholder="Enter or paste the content here"
                  value={kbForm.value}
                  onChange={(e) =>
                    setKbForm((prev) => ({ ...prev, value: e.target.value }))
                  }
                />
              </div>

              <div className="flex justify-end mt-5">
                <Button
                  onClick={async () => {
                    const newForm = {
                      name: kbForm.name,
                      description: kbForm.description,
                      value: kbForm.value,
                    };

                    await createKb(newForm);
                    setOpenDialog(false);
                    await asyncExec();
                  }}
                >
                  Add to Knowledge Base
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="w-full py-10">
        {loadingKbs && (
          <div className="w-full flex items-center justify-center my-3">
            <Spinner />
          </div>
        )}

        {kbs.length === 0 && !loadingKbs && (
          <div className="p-3 w-full border border-[#0D33F1] mt-10 rounded-md shadow-md bg-[#0D33F1]/5">
            <div className="flex gap-3 items-center justify-start">
              <Flag className="text-[#0D33F1]" size={20} />
              <h2 className="text-lg font-medium">
                Get Started with Knowledge Base
              </h2>
            </div>

            <div className="mt-2">
              <p className="text-sm text-zinc-500">
                Add facts about your brand to your Knowledge Base. Use your
                Knowledge Base when creating decks so that FlashDocs can write
                factually about your business, products and more!
              </p>
            </div>
          </div>
        )}

        {searchResults().length > 0 && (
          <div className="border rounded-md">
            <Table>
              <TableHeader className="bg-zinc-100">
                <TableRow>
                  <TableHead className="w-1/3">Name</TableHead>
                  <TableHead>Created by</TableHead>
                  <TableHead>Created at</TableHead>
                  <TableHead>Updated at</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {searchResults().map((kb, i) => (
                  <TableRow key={i}>
                    <TableCell
                      role="button"
                      className="font-medium w-1/3"
                      onClick={() => {
                        setSelectedKb(kb);
                      }}
                    >
                      {kb.name}
                      <p className="text-xs text-zinc-500">
                        {kb.description.substring(0, 50)}...
                      </p>
                    </TableCell>
                    <TableCell>{kb.created_by || "unknown"}</TableCell>
                    <TableCell>
                      {new Date(kb.created_at).toDateString()}
                    </TableCell>
                    <TableCell>
                      {new Date(kb.updated_at).toDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-5">
                        <button
                          onClick={() => {
                            setSelectedKb(kb);
                            setEditMode(true);
                          }}
                        >
                          <Pencil className="text-zinc-500" size={15} />
                        </button>
                        <button
                          onClick={async () => {
                            await deleteKb(kb.id);
                            await asyncExec();
                          }}
                        >
                          <Trash className="text-zinc-500" size={15} />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {selectedKb && Object.keys(selectedKb).length > 0 && (
          <Dialog
            open={selectedKb && Object.keys(selectedKb).length > 0}
            onOpenChange={(open) => {
              if (!open) {
                setSelectedKb(null);
              }
            }}
          >
            <DialogContent className="min-w-[900px] text-zinc-700 overflow-x-hidden overflow-y-scroll max-h-[800px] no-scrollbar">
              <DialogHeader className="border-b py-5">
                <DialogTitle className="font-medium">
                  Knowledge Base
                </DialogTitle>
              </DialogHeader>

              <div className="py-5">
                <div className="mb-7">
                  <div>
                    {editMode ? (
                      <div className="flex items-center justify-between gap-3">
                        <input
                          type="text"
                          className="border border-zinc-500 rounded-md px-2 py-1 w-full"
                          value={selectedKb.name}
                          onChange={(e) => {
                            setSelectedKb((prev: any) => ({
                              ...prev,
                              name: e.target.value,
                            }));
                          }}
                        />
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <p className="font-medium">{selectedKb.name}</p>
                      </div>
                    )}
                  </div>
                  <div>
                    {editMode ? (
                      <div className="flex items-center justify-between gap-3">
                        <Textarea
                          className="border border-zinc-500 my-3 text-base rounded-md px-2 py-1"
                          value={selectedKb.description}
                          onChange={(e) => {
                            setSelectedKb((prev: any) => ({
                              ...prev,
                              description: e.target.value,
                            }));
                          }}
                        />
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <p className="text-sm text-zinc-500">
                          {selectedKb.description}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="mb-7">
                  <label
                    htmlFor="created-template"
                    className="font-medium block"
                  >
                    Created By
                  </label>
                  {selectedKb.created_by || "Unknown"}
                </div>
                <div className="mb-7">
                  <label
                    htmlFor="created-template"
                    className="font-medium block"
                  >
                    Created At
                  </label>
                  {selectedKb.created_at}
                </div>
                <div className="mb-7">
                  <label
                    htmlFor="created-template"
                    className="font-medium block"
                  >
                    Updated At
                  </label>
                  {selectedKb.created_at}
                </div>
                <div className="mb-7">
                  <div>
                    {editMode ? (
                      <div>
                        <div className="flex items-center justify-start gap-3">
                          <p className="font-medium">Content</p>
                        </div>
                        <Textarea
                          className="border border-zinc-500 rounded-md px-2 py-1 text-base"
                          value={selectedKb.value}
                          rows={10}
                          onChange={(e) => {
                            setSelectedKb((prev: any) => ({
                              ...prev,
                              value: e.target.value,
                            }));
                          }}
                        />
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <p className="font-medium">Content</p>
                      </div>
                    )}
                  </div>
                  {!editMode && (
                    <div
                      className="text-zinc-600 markdown"
                      dangerouslySetInnerHTML={{
                        __html: marked(selectedKb.value, { async: false }),
                      }}
                    ></div>
                  )}

                  {editMode && (
                    <div className="flex items-center justify-end py-3">
                      <Button
                        onClick={async () => {
                          const originalKb = kbs.find(
                            (kb) => kb.id === selectedKb.id,
                          );

                          if (!originalKb) return;

                          if (
                            originalKb.value === selectedKb.value &&
                            originalKb.name === selectedKb.name &&
                            originalKb.description === selectedKb.description
                          ) {
                            setEditMode(false);
                            return;
                          }

                          await editKnowledgeBase(selectedKb.id, selectedKb);
                          await asyncExec();
                        }}
                      >
                        Save
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
};

export default KnowledgeBase;
